/**
 * Dealer profile SEO metadata (Dealer Profile M11).
 * Reuses shared AutoMapa SEO utilities from client/src/seo.
 */
import {
  AUTOMAPA_DEFAULT_OG_IMAGE,
  AUTOMAPA_OG_TYPE,
  PUBLIC_DEALER_DIRECTORY_PATH,
  PUBLIC_SEARCH_PATH,
} from "../../seo/seoConstants";
import { buildAbsoluteUrl, resolveOgImageUrl } from "../../seo/canonicalUrls";
import {
  buildMetaDescription,
  buildPageTitle,
  buildOpenGraphBundle,
  buildTwitterBundle,
  robotsDirective,
  sanitizeSeoText,
} from "../../seo/seoUtils";
import { buildBreadcrumbJsonLd, combineStructuredData } from "../../seo/structuredData";
import {
  DEALER_PROFILE_FALLBACK_COPY,
  DEALER_PROFILE_PAGE_STATE,
} from "../../constants/dealerProfileConstants";
import { DEALER_PROFILE_ROUTE_STATUS } from "./dealerProfileRoute";
import { getDealerProfileHref, isValidDealerSlug } from "./dealerSlug";
import { getDealerProfileSeoIndexDecision } from "./dealerPublicState";
import { normalizeDealerProfile } from "./normalizeDealerProfile";

export const DEALER_PROFILE_SEO_COPY_ES = {
  fallbackTitle: "Concesionario en AutoMapa",
  fallbackDescription:
    "Consulta perfiles de concesionarios y vehículos usados disponibles en AutoMapa.",
  notFoundTitle: "Concesionario no encontrado",
  unavailableTitle: "Concesionario no disponible",
  loadingTitle: "Concesionario | AutoMapa",
  loadingDescription: "Consulta perfiles de concesionarios en AutoMapa.",
  errorTitle: "Concesionario no disponible",
  errorDescription: "No pudimos cargar este concesionario. Sigue buscando vehículos usados en AutoMapa.",
};

const BLOCKED_INDEX_STATES = new Set([
  DEALER_PROFILE_PAGE_STATE.HIDDEN,
  DEALER_PROFILE_PAGE_STATE.INACTIVE,
  DEALER_PROFILE_PAGE_STATE.SUSPENDED,
  DEALER_PROFILE_PAGE_STATE.REJECTED,
  DEALER_PROFILE_PAGE_STATE.DELETED,
  DEALER_PROFILE_PAGE_STATE.INVALID,
  DEALER_PROFILE_PAGE_STATE.NOT_FOUND,
  DEALER_PROFILE_PAGE_STATE.UNKNOWN,
  DEALER_PROFILE_PAGE_STATE.ERROR,
]);

function resolveDealerDisplayName(dealer) {
  return (
    sanitizeSeoText(dealer?.displayName || dealer?.name || dealer?.businessName) ||
    DEALER_PROFILE_FALLBACK_COPY.name
  );
}

function resolveActiveInventoryCount(dealer, inventorySummary, options = {}) {
  if (inventorySummary?.activeInventoryCount != null) {
    return inventorySummary.activeInventoryCount;
  }
  if (options.activeInventoryCount != null) {
    return options.activeInventoryCount;
  }
  return dealer?.activeInventoryCount ?? dealer?.inventoryCount ?? 0;
}

/**
 * Canonical dealer profile path without query params (inventory filters, UTM, etc.).
 */
export function buildDealerProfileCanonicalHref(dealer, options = {}) {
  if (!dealer) return null;
  const href = getDealerProfileHref(dealer, { usePublicRoute: options.usePublicRoute !== false });
  if (!href || !isValidDealerSlug(dealer?.slug)) return null;
  return href.split("?")[0];
}

export function buildDealerProfileBreadcrumbs(dealer, options = {}) {
  const canonicalHref = buildDealerProfileCanonicalHref(dealer, options);
  const dealerName = resolveDealerDisplayName(dealer);

  const items = [
    { label: "Inicio", href: "/" },
    { label: "Carros usados", href: PUBLIC_SEARCH_PATH },
    { label: "Concesionarios", href: PUBLIC_DEALER_DIRECTORY_PATH },
  ];

  if (canonicalHref && dealerName) {
    items.push({ label: dealerName, href: canonicalHref });
  }

  return items;
}

export function buildDealerProfileSeoTitle(dealer, publicState, inventorySummary, options = {}) {
  const name = resolveDealerDisplayName(dealer);
  const city = sanitizeSeoText(dealer?.city);
  const count = resolveActiveInventoryCount(dealer, inventorySummary, options);
  const hasInventory = count > 0 && publicState?.canShowInventory !== false;

  if (!dealer || !publicState?.canShowProfile) {
    return buildPageTitle(DEALER_PROFILE_SEO_COPY_ES.fallbackTitle);
  }

  if (publicState.state === DEALER_PROFILE_PAGE_STATE.PUBLIC_NO_INVENTORY || !hasInventory) {
    return buildPageTitle(`${name} | Perfil del concesionario en AutoMapa`);
  }

  if (city) {
    return buildPageTitle(`${name} en ${city} | Vehículos disponibles en AutoMapa`);
  }

  return buildPageTitle(`${name}: ${count} vehículos disponibles | AutoMapa`);
}

export function buildDealerProfileSeoDescription(dealer, publicState, inventorySummary, options = {}) {
  const name = resolveDealerDisplayName(dealer);
  const city = sanitizeSeoText(dealer?.city);
  const count = resolveActiveInventoryCount(dealer, inventorySummary, options);
  const hasInventory = count > 0 && publicState?.canShowInventory !== false;

  if (!dealer || !publicState?.canShowProfile) {
    return buildMetaDescription(DEALER_PROFILE_SEO_COPY_ES.fallbackDescription);
  }

  if (publicState.state === DEALER_PROFILE_PAGE_STATE.PUBLIC_NO_INVENTORY || !hasInventory) {
    return buildMetaDescription(
      `Consulta el perfil de ${name} en AutoMapa y sigue buscando vehículos usados disponibles.`
    );
  }

  if (city) {
    return buildMetaDescription(
      `Encuentra vehículos disponibles de ${name} en ${city}. Compara precio, kilometraje, señales de riesgo y contacta de forma segura desde AutoMapa.`
    );
  }

  return buildMetaDescription(
    `Consulta vehículos disponibles de ${name} en AutoMapa. Revisa precios, características, señales de riesgo y datos del concesionario antes de contactar.`
  );
}

export function resolveDealerProfileOgImage(dealer) {
  const candidate = dealer?.logoUrl || dealer?.coverImageUrl || null;
  return resolveOgImageUrl(candidate || AUTOMAPA_DEFAULT_OG_IMAGE);
}

function isSafeDealerLogoUrl(url) {
  if (!url) return false;
  const resolved = resolveOgImageUrl(url);
  const fallback = resolveOgImageUrl(AUTOMAPA_DEFAULT_OG_IMAGE);
  return Boolean(resolved && resolved !== fallback);
}

/**
 * Safe AutoDealer JSON-LD — no phone/WhatsApp/email, no unconfirmed street address.
 * TODO(DealerProfile Future): Expand schema when verified public address fields exist.
 */
export function buildSafeDealerProfileJsonLd(dealer, canonicalHref, description) {
  if (!dealer?.slug) return null;

  const name = resolveDealerDisplayName(dealer);
  if (!name || name === DEALER_PROFILE_FALLBACK_COPY.name) return null;

  const url = buildAbsoluteUrl(canonicalHref);
  const schema = {
    "@context": "https://schema.org",
    "@type": "AutoDealer",
    name,
    url,
    description: buildMetaDescription(description, { maxLength: 240 }),
  };

  const logo = isSafeDealerLogoUrl(dealer.logoUrl) ? resolveOgImageUrl(dealer.logoUrl) : null;
  const image = isSafeDealerLogoUrl(dealer.coverImageUrl)
    ? resolveOgImageUrl(dealer.coverImageUrl)
    : logo;
  if (image) schema.image = image;

  const locality = sanitizeSeoText(dealer.city);
  const region = sanitizeSeoText(dealer.department);
  if (locality || region) {
    schema.address = {
      "@type": "PostalAddress",
      addressCountry: "CO",
      ...(locality ? { addressLocality: locality } : {}),
      ...(region ? { addressRegion: region } : {}),
    };
  }

  return schema;
}

export function buildDealerProfileStructuredData(dealer, publicState, metadata, inventorySummary = null) {
  if (!publicState?.canShowProfile || !metadata?.canonicalHref) {
    return buildBreadcrumbJsonLd(metadata?.breadcrumbItems || []);
  }

  const indexDecision = getDealerProfileSeoIndexDecision(publicState, inventorySummary);
  const breadcrumbs = buildBreadcrumbJsonLd(metadata.breadcrumbItems);

  if (!indexDecision.shouldIndex) {
    return breadcrumbs;
  }

  const dealerSchema = buildSafeDealerProfileJsonLd(
    dealer,
    metadata.canonicalHref,
    metadata.description
  );

  return combineStructuredData(dealerSchema, breadcrumbs);
}

export function getDealerProfileRobots(publicState, inventorySummary = null) {
  const indexDecision = getDealerProfileSeoIndexDecision(publicState, inventorySummary);
  return robotsDirective(indexDecision.shouldIndex, { follow: true });
}

export function buildDealerProfileSeo(dealerInput, publicState, inventorySummary = null, options = {}) {
  const dealer = dealerInput ? normalizeDealerProfile(dealerInput, options) : null;
  const canonicalHref = dealer ? buildDealerProfileCanonicalHref(dealer, options) : null;
  const indexDecision = getDealerProfileSeoIndexDecision(publicState, inventorySummary);
  const title = buildDealerProfileSeoTitle(dealer, publicState, inventorySummary, options);
  const description = buildDealerProfileSeoDescription(dealer, publicState, inventorySummary, options);
  const breadcrumbItems =
    dealer && publicState?.canShowProfile
      ? buildDealerProfileBreadcrumbs(dealer, options)
      : [
          { label: "Inicio", href: "/" },
          { label: "Carros usados", href: PUBLIC_SEARCH_PATH },
        ];
  const ogImageUrl = resolveDealerProfileOgImage(dealer);
  const canonicalUrl = canonicalHref ? buildAbsoluteUrl(canonicalHref) : undefined;

  const metadata = {
    title,
    description,
    canonicalHref,
    canonicalUrl,
    robots: getDealerProfileRobots(publicState, inventorySummary),
    noindexRecommended: !indexDecision.shouldIndex,
    noindexReason: indexDecision.noindexReason,
    ogTitle: title,
    ogDescription: description,
    ogImageUrl,
    ogType: AUTOMAPA_OG_TYPE,
    breadcrumbItems,
    alternates: canonicalUrl ? { canonical: canonicalUrl } : {},
    warnings: [],
  };

  metadata.openGraph = buildOpenGraphBundle({
    title,
    description,
    image: ogImageUrl,
    url: canonicalUrl,
    type: AUTOMAPA_OG_TYPE,
  });

  metadata.twitter = buildTwitterBundle({
    title,
    description,
    image: ogImageUrl,
  });

  metadata.structuredData = buildDealerProfileStructuredData(
    dealer,
    publicState,
    metadata,
    inventorySummary
  );

  if (!canonicalHref && publicState?.canShowProfile) {
    metadata.warnings.push("missing_canonical_slug");
  }

  return metadata;
}

function buildStateSeo({ titleCore, description, robotsIndex = false, breadcrumbItems }) {
  const title = buildPageTitle(titleCore);
  const resolvedDescription = buildMetaDescription(description);
  const ogImageUrl = resolveOgImageUrl(AUTOMAPA_DEFAULT_OG_IMAGE);

  return {
    title,
    description: resolvedDescription,
    canonicalHref: null,
    canonicalUrl: undefined,
    robots: robotsDirective(robotsIndex, { follow: true }),
    noindexRecommended: !robotsIndex,
    noindexReason: robotsIndex ? null : "state_blocked",
    ogTitle: title,
    ogDescription: resolvedDescription,
    ogImageUrl,
    ogType: AUTOMAPA_OG_TYPE,
    breadcrumbItems,
    alternates: {},
    warnings: [],
    openGraph: buildOpenGraphBundle({
      title,
      description: resolvedDescription,
      image: ogImageUrl,
      type: AUTOMAPA_OG_TYPE,
    }),
    twitter: buildTwitterBundle({
      title,
      description: resolvedDescription,
      image: ogImageUrl,
    }),
    structuredData: buildBreadcrumbJsonLd(breadcrumbItems),
  };
}

export function getDealerProfileLoadingMetadata() {
  return buildStateSeo({
    titleCore: DEALER_PROFILE_SEO_COPY_ES.loadingTitle,
    description: DEALER_PROFILE_SEO_COPY_ES.loadingDescription,
    robotsIndex: false,
    breadcrumbItems: [
      { label: "Inicio", href: "/" },
      { label: "Carros usados", href: PUBLIC_SEARCH_PATH },
    ],
  });
}

export function getDealerProfileUnavailableMetadata(publicState) {
  const isNotFound =
    !publicState ||
    publicState.state === DEALER_PROFILE_PAGE_STATE.NOT_FOUND ||
    publicState.state === DEALER_PROFILE_PAGE_STATE.INVALID;

  return buildStateSeo({
    titleCore: isNotFound
      ? DEALER_PROFILE_SEO_COPY_ES.notFoundTitle
      : DEALER_PROFILE_SEO_COPY_ES.unavailableTitle,
    description: isNotFound
      ? "No encontramos este concesionario en AutoMapa. Puedes seguir buscando vehículos usados disponibles."
      : "Este concesionario no está disponible en AutoMapa. Puedes seguir buscando vehículos usados disponibles.",
    robotsIndex: false,
    breadcrumbItems: [
      { label: "Inicio", href: "/" },
      { label: "Carros usados", href: PUBLIC_SEARCH_PATH },
    ],
  });
}

export function getDealerProfileErrorMetadata() {
  return buildStateSeo({
    titleCore: DEALER_PROFILE_SEO_COPY_ES.errorTitle,
    description: DEALER_PROFILE_SEO_COPY_ES.errorDescription,
    robotsIndex: false,
    breadcrumbItems: [
      { label: "Inicio", href: "/" },
      { label: "Carros usados", href: PUBLIC_SEARCH_PATH },
    ],
  });
}

/**
 * Resolve SEO metadata for DealerProfilePage across loading, error, and profile states.
 */
export function getDealerProfileMetadata(context = {}) {
  const {
    dealer = null,
    publicState = null,
    inventorySummary = null,
    pageState = "ready",
    routeStatus = null,
    options = {},
  } = context;

  if (pageState === "loading") {
    return getDealerProfileLoadingMetadata();
  }

  if (
    routeStatus === DEALER_PROFILE_ROUTE_STATUS.ERROR ||
    publicState?.state === DEALER_PROFILE_PAGE_STATE.ERROR
  ) {
    return getDealerProfileErrorMetadata();
  }

  if (
    !publicState?.canShowProfile ||
    BLOCKED_INDEX_STATES.has(publicState?.state) ||
    routeStatus === DEALER_PROFILE_ROUTE_STATUS.NOT_FOUND ||
    routeStatus === DEALER_PROFILE_ROUTE_STATUS.INVALID_SLUG
  ) {
    return getDealerProfileUnavailableMetadata(publicState);
  }

  if (publicState?.showUnavailableState) {
    return getDealerProfileUnavailableMetadata(publicState);
  }

  return buildDealerProfileSeo(dealer, publicState, inventorySummary, options);
}
